import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { crmApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import type { Customer360 } from '@/types/crm';
import { showApiError } from '@/utils/apiError';

export function Customer360Page() {
  const { customerId } = useParams<{ customerId: string }>();
  const [data, setData] = useState<Customer360 | null>(null);

  useEffect(() => {
    if (!customerId) return;
    crmApi
      .customer360(Number(customerId))
      .then(setData)
      .catch((err) => showApiError(err, 'Failed to load Customer 360'));
  }, [customerId]);

  if (!data) {
    return <div className="p-6 text-slate-500 dark:text-slate-400">Loading customer profile…</div>;
  }

  return (
    <div>
      <PageHeader
        title={data.profile.name}
        description="Customer 360 — unified Fattah timeline"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'CRM', to: '/crm' },
          { label: data.profile.name },
        ]}
      />

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-1 panel space-y-3">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100">Profile</h3>
          <p className="text-sm">{data.profile.phone || '—'}</p>
          <p className="text-sm">{data.profile.email || '—'}</p>
          <div className="grid grid-cols-2 gap-2 text-sm pt-2">
            <div>
              <span className="text-slate-500">Open deals</span>
              <p className="font-medium">{data.summary.openDeals}</p>
            </div>
            <div>
              <span className="text-slate-500">Open tickets</span>
              <p className="font-medium">{data.summary.openTickets ?? 0}</p>
            </div>
            <div>
              <span className="text-slate-500">Activities</span>
              <p className="font-medium">{data.summary.totalActivities}</p>
            </div>
            <div>
              <span className="text-slate-500">Invoices</span>
              <p className="font-medium">{data.summary.invoiceCount ?? 0}</p>
            </div>
            <div>
              <span className="text-slate-500">Quotes</span>
              <p className="font-medium">{data.summary.quoteCount ?? 0}</p>
            </div>
            <div>
              <span className="text-slate-500">Won revenue</span>
              <p className="font-medium">{data.summary.totalWonRevenue ?? 0}</p>
            </div>
          </div>
          {customerId && (
            <Link
              to={`/desk/support-360/${customerId}`}
              className="inline-block text-sm text-primary-600 hover:underline pt-2"
            >
              Open Customer Support 360 →
            </Link>
          )}
        </div>

        <div className="lg:col-span-2 panel">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-3">Timeline</h3>
          <ul className="space-y-3 max-h-96 overflow-y-auto">
            {data.timeline.length === 0 && (
              <li className="text-sm text-slate-500">No activities yet</li>
            )}
            {data.timeline.map((a) => (
              <li key={a.id} className="border-l-2 border-primary-200 pl-3">
                <p className="text-xs text-slate-500">{a.sourceModule} · {a.activityType}</p>
                <p className="font-medium text-sm">{a.title}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid gap-4 mt-4 lg:grid-cols-3">
        <div className="panel">
          <h3 className="font-semibold mb-2">Finance — Quotes</h3>
          <ul className="text-sm space-y-1">
            {(data.quotes ?? []).map((q) => (
              <li key={q.id}>
                {q.invoiceNumber} — {q.statusCode} ({q.totalAmount})
              </li>
            ))}
            {!data.quotes?.length && <li className="text-slate-500">No quotes</li>}
          </ul>
        </div>
        <div className="panel">
          <h3 className="font-semibold mb-2">Finance — Invoices</h3>
          <ul className="text-sm space-y-1">
            {(data.invoices ?? []).map((inv) => (
              <li key={inv.id}>
                {inv.invoiceNumber} — {inv.statusCode} ({inv.totalAmount})
              </li>
            ))}
            {!data.invoices?.length && <li className="text-slate-500">No finance invoices</li>}
          </ul>
        </div>
        <div className="panel">
          <h3 className="font-semibold mb-2">Desk tickets</h3>
          <ul className="text-sm space-y-1">
            {(data.tickets ?? []).map((t) => (
              <li key={t.id}>
                {t.ticketNumber ?? t.id}: {t.subject} {t.closed ? '(closed)' : '(open)'}
              </li>
            ))}
            {!data.tickets?.length && <li className="text-slate-500">No tickets</li>}
          </ul>
        </div>
      </div>

      <div className="grid gap-4 mt-4 lg:grid-cols-2">
        <div className="panel">
          <h3 className="font-semibold mb-2">Contacts</h3>
          <ul className="text-sm space-y-1">
            {data.contacts.map((c) => (
              <li key={c.id}>
                {c.firstName} {c.lastName} {c.primaryContact ? '(primary)' : ''}
              </li>
            ))}
            {data.contacts.length === 0 && <li className="text-slate-500">No contacts</li>}
          </ul>
        </div>
        <div className="panel">
          <h3 className="font-semibold mb-2">Deals</h3>
          <ul className="text-sm space-y-1">
            {data.deals.map((d) => (
              <li key={d.id}>
                <Link to="/crm/deals" className="text-primary-600 hover:underline">
                  {d.dealName}
                </Link>{' '}
                — {d.dealStatus} ({d.expectedRevenue})
              </li>
            ))}
            {data.deals.length === 0 && <li className="text-slate-500">No deals</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
